<template>
  <div class="home-slider">
    <div class="slider-container">
      <div
        v-for="(slide, index) in slides"
        :key="index"
        class="slide"
        :class="{ active: index === currentSlide }"
      >
        <img
          :src="slide.image"
          :alt="slide.title"
          class="slide-image"
        >
        <div
          v-if="index === currentSlide"
          class="slide-content"
        >
          <h2
            class="slide-title animate__animated"
            :class="animationClasses"
          >
            {{ slide.title }}
          </h2>
          <p
            class="slide-description animate__animated"
            :class="animationClasses"
          >
            {{ slide.description }}
          </p>
          <!-- Button yerine a kullanıyoruz -->
          <a
            v-if="slide.buttonText"
            href="#"
            class="slide-button animate__animated animate__fadeInUp"
            @click.prevent
          >
            {{ slide.buttonText }}
          </a>
        </div>
      </div>

      <!-- Navigasyon Noktaları -->
      <div class="slider-dots">
        <span
          v-for="(slide, index) in slides"
          :key="`dot-${index}`"
          class="dot"
          :class="{ active: index === currentSlide }"
          @click="goToSlide(index)"
        />
      </div>

      <!-- Yön Okları -->
      <button
        class="slider-arrow prev"
        @click="prevSlide"
      >
        <span class="arrow-icon">&#10094;</span>
      </button>
      <button
        class="slider-arrow next"
        @click="nextSlide"
      >
        <span class="arrow-icon">&#10095;</span>
      </button>
    </div>
  </div>
</template>

<script setup>
  import { ref, onMounted, onUnmounted, watch } from 'vue';

  // Slider ayarları için prop'lar
  // eslint-disable-next-line no-undef
  const props = defineProps({
    // Otomatik geçiş için süre (milisaniye)
    transitionInterval: {
      type: Number,
      default: 50000,
    },
    // Otomatik geçişi aktif et/etme
    autoplay: {
      type: Boolean,
      default: true,
    },
    // Özel slide içeriği
    customSlides: {
      type: Array,
      default: () => [],
    },
    // Animasyon türü
    animationType: {
      type: String,
      default: 'animate__lightSpeedInRight',
    },
  });

  // Animasyon sınıflarını dinamik olarak ayarla
  const animationClasses = ref(props.animationType);

  // Varsayılan slider içeriği
  const defaultSlides = [
    {
      image:
        'https://firebasestorage.googleapis.com/v0/b/petqueen-v3.firebasestorage.app/o/public%2Fassets%2Fslider%2F1.png?alt=media&',
      title: 'Find Your Furry Friend',
      description: 'The perfect place to meet your next pet companion',
      buttonText: 'Discover',
    },
    {
      image:
        'https://firebasestorage.googleapis.com/v0/b/petqueen-v3.firebasestorage.app/o/public%2Fassets%2Fslider%2F2.png?alt=media&',
      title: 'Happy Paws',
      description: 'Adorable companions waiting for their forever homes',
      buttonText: 'Browse Ads',
    },
    {
      image:
        'https://firebasestorage.googleapis.com/v0/b/petqueen-v3.firebasestorage.app/o/public%2Fassets%2Fslider%2F3.png?alt=media&',
      title: 'Unique Friendship',
      description: 'Find friends that will add color to your life',
      buttonText: 'Get Started',
    },
    {
      image:
        'https://firebasestorage.googleapis.com/v0/b/petqueen-v3.firebasestorage.app/o/public%2Fassets%2Fslider%2F4.png?alt=media&',
      title: 'New Family Member',
      description: "You're in the right place to find the newest addition to your family",
      buttonText: 'Explore',
    },
  ];

  // Kullanıcı özel slide'lar tanımladıysa onları kullan, yoksa varsayılanları
  const slides = ref(props.customSlides.length > 0 ? props.customSlides : defaultSlides);
  const currentSlide = ref(0);
  let slideInterval = null;

  // Slide değiştiğinde animasyonu yeniden başlat
  watch(currentSlide, () => {
    resetAnimation();
  });

  // Animasyonu yeniden başlatma fonksiyonu
  const resetAnimation = () => {
    // Animasyon sınıfını geçici olarak kaldır
    animationClasses.value = '';

    // Bir sonraki frame'de animasyon sınıfını tekrar ekle (DOM güncellenmesi için)
    setTimeout(() => {
      animationClasses.value = props.animationType;
    }, 10);
  };

  // Slider kontrol fonksiyonları
  const nextSlide = () => {
    currentSlide.value = (currentSlide.value + 1) % slides.value.length;
  };

  const prevSlide = () => {
    currentSlide.value = (currentSlide.value - 1 + slides.value.length) % slides.value.length;
  };

  const goToSlide = (index) => {
    currentSlide.value = index;
  };

  // Otomatik slider geçişini başlat
  const startSlideTimer = () => {
    if (props.autoplay) {
      slideInterval = setInterval(() => {
        nextSlide();
      }, props.transitionInterval);
    }
  };

  // Component mount olduğunda slider'ı başlat
  onMounted(() => {
    startSlideTimer();
  });

  // Component unmount olduğunda timer'ı temizle
  onUnmounted(() => {
    if (slideInterval) {
      clearInterval(slideInterval);
    }
  });
</script>

<style scoped>
  .home-slider {
    width: 100%;
    overflow: hidden;
    position: relative;
    margin-bottom: 30px;
  }

  .slider-container {
    position: relative;
    width: 100%;
    height: 500px;
    /* Slider yüksekliği */
    overflow: hidden;
  }

  .slide {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    transition: opacity 0.8s ease;
    overflow: hidden;
  }

  .slide.active {
    opacity: 1;
  }

  .slide-image {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  .slide-content {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translateY(-50%);
    max-width: 1200px;
    color: #fff;
    text-shadow: 1px 1px 3px rgba(0, 0, 0, 0.5);
    padding: 20px;
    border-radius: 5px;
  }

  .slide-title {
    font-size: 2.5rem;
    font-weight: 700;
    margin-bottom: 10px;
  }

  .slide-description {
    font-size: 1.2rem;
    margin-bottom: 20px;
    line-height: 1.5;
  }

  .slide-button {
    background-color: rgba(58, 12, 75, 0.8);
    color: white !important;
    border: none;
    padding: 12px 24px;
    font-size: 1rem;
    border-radius: 30px;
    cursor: pointer !important;
    transition: all 0.3s ease;
    font-weight: bold;
    display: inline-block;
    text-decoration: none !important;
    position: relative;
    z-index: 2;
  }

  .slide-button:hover {
    background-color: #e02c50 !important;
    transform: scale(1.05);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
    color: white !important;
  }

  .slide-button:active {
    background-color: #c01c40 !important;
    transform: scale(0.98);
  }

  .slider-dots {
    position: absolute;
    bottom: 20px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    gap: 10px;
  }

  .dot {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background-color: rgba(255, 255, 255, 0.5);
    cursor: pointer;
    transition: background-color 0.3s ease;
  }

  .dot.active {
    background-color: #ff385c;
  }

  .slider-arrow {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    background-color: rgba(255, 255, 255, 0.6);
    border: none;
    width: 50px;
    height: 50px;
    border-radius: 50%;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background-color 0.3s ease;
    z-index: 10;
  }

  .slider-arrow:hover {
    background-color: rgba(255, 255, 255, 0.9);
  }

  .slider-arrow.prev {
    left: 20px;
  }

  .slider-arrow.next {
    right: 20px;
  }

  .arrow-icon {
    font-size: 20px;
    font-weight: bold;
    color: #333;
  }

  /* Mobil uyumluluk */
  @media (max-width: 768px) {
    .slider-container {
      height: 350px;
    }

    .slide-content {
      left: 20px;
      right: 20px;
      max-width: calc(100% - 40px);
      text-align: center;
    }

    .slide-title {
      font-size: 1.8rem;
    }

    .slide-description {
      font-size: 1rem;
    }

    .slider-arrow {
      width: 40px;
      height: 40px;
    }
  }
</style>
